import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Scale, ArrowLeft, Plus, FileText, Eye, Trash2, Search, Clock, MoreVertical } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useDocumentosLegales } from "@/hooks/useDocumentosLegales";
import { useStatePersistence } from "@/hooks/useStatePersistence";
import SubirDocumentoModal from "@/components/legal/SubirDocumentoModal";
import DocumentViewer from "@/components/legal/DocumentViewer";
import Footer from "@/components/Footer";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const Legal = () => {
  const navigate = useNavigate();
  const { signOut, user } = useAuth();
  const { documentos, loading, deleteDocumento, getEstadisticas } = useDocumentosLegales();
  
  // Persistir estado de modales y búsqueda
  const [isUploadModalOpen, setIsUploadModalOpen, clearUploadModal] = useStatePersistence({
    key: 'legal_upload_modal',
    initialValue: false,
    userId: user?.id
  });
  
  const [searchTerm, setSearchTerm, clearSearch] = useStatePersistence({
    key: 'legal_search',
    initialValue: '',
    userId: user?.id
  });
  
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [tipoFilter, setTipoFilter] = useState<'all' | 'Autos de Seguimientos' | 'Actas de Oralidad de Seguimiento' | 'Resoluciones'>('all');

  const estadisticas = getEstadisticas();

  const handleCardFilterClick = (tipo: 'all' | 'Autos de Seguimientos' | 'Actas de Oralidad de Seguimiento' | 'Resoluciones') => {
    setTipoFilter(tipo);
  };

  let filteredDocumentos = documentos.filter(doc => 
    doc.nombre_documento.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.tipo_documento.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.nombre_archivo.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Aplicar filtro de tipo si está activo
  if (tipoFilter !== 'all') {
    filteredDocumentos = filteredDocumentos.filter(doc => doc.tipo_documento === tipoFilter);
  }

  const handleViewDocument = (documento: any) => {
    setSelectedDocument(documento);
    setIsViewerOpen(true);
  };

  const handleDeleteDocument = async (documento: any) => {
    await deleteDocumento(documento);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString(); // Usa el formato del sistema
  };

  const getTipoBadge = (tipo: string) => {
    const colors = {
      'Autos de Seguimientos': 'bg-blue-100 text-blue-700 border-blue-200',
      'Actas de Oralidad de Seguimiento': 'bg-green-100 text-green-700 border-green-200',
      'Resoluciones': 'bg-purple-100 text-purple-700 border-purple-200'
    };

    return (
      <Badge className={colors[tipo as keyof typeof colors] || 'bg-gray-100 text-gray-700'}>
        {tipo}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-border shadow-sm">
        <div className="container mx-auto px-4 py-4">
          {/* Mobile/Tablet Layout */}
          <div className="lg:hidden relative flex flex-col items-center gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="absolute top-0 right-0">
                  <MoreVertical className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-background z-50">
                <DropdownMenuItem onClick={() => navigate("/home")}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Volver
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setIsUploadModalOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Subir Documento
                </DropdownMenuItem>
                <DropdownMenuItem onClick={signOut}>
                  Cerrar Sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Scale className="w-10 h-10 text-purple-600" />
            <div className="text-center">
              <h1 className="text-xl font-bold text-foreground">Módulo Legal</h1>
              <p className="text-sm text-muted-foreground">Gestión de Documentos Legales</p>
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden lg:flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/home")}
                className="mr-2"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver
              </Button>
              <Scale className="w-8 h-8 text-purple-600" />
              <div>
                <h1 className="text-xl font-bold text-foreground">Módulo Legal</h1>
                <p className="text-sm text-muted-foreground">Gestión de Documentos Legales</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button onClick={() => setIsUploadModalOpen(true)} size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Subir Documento
              </Button>
              <Button variant="outline" size="sm" onClick={signOut}>
                Cerrar Sesión
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-32 lg:pt-24 pb-12">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            {/* Welcome Section */}
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-foreground mb-2">
                Documentos Legales
              </h2>
              <p className="text-lg text-muted-foreground">
                Gestión centralizada de autos, actas y resoluciones
              </p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <Card 
                className={`transition-all cursor-pointer ${
                  tipoFilter === 'all' 
                    ? 'ring-2 ring-orange-500 shadow-lg scale-105' 
                    : 'hover:shadow-md hover:scale-102'
                }`}
                onClick={() => handleCardFilterClick('all')}
              >
                <CardContent className="pt-6">
                  <div className="flex items-center">
                    <FileText className="w-8 h-8 text-purple-600" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-muted-foreground">Total</p>
                      <p className="text-2xl font-bold">{estadisticas.total}</p>
                    </div>
                  </div>
                  {tipoFilter === 'all' && (
                    <p className="text-xs text-muted-foreground mt-2">Filtro activo</p>
                  )}
                </CardContent>
              </Card>

              <Card 
                className={`transition-all cursor-pointer ${
                  tipoFilter === 'Autos de Seguimientos' 
                    ? 'ring-2 ring-orange-500 shadow-lg scale-105' 
                    : 'hover:shadow-md hover:scale-102'
                }`}
                onClick={() => handleCardFilterClick('Autos de Seguimientos')}
              >
                <CardContent className="pt-6">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <FileText className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-muted-foreground">Autos</p>
                      <p className="text-2xl font-bold text-blue-600">{estadisticas.autosSeguimientos}</p>
                    </div>
                  </div>
                  {tipoFilter === 'Autos de Seguimientos' && (
                    <p className="text-xs text-muted-foreground mt-2">Filtro activo</p>
                  )}
                </CardContent>
              </Card>

              <Card 
                className={`transition-all cursor-pointer ${
                  tipoFilter === 'Actas de Oralidad de Seguimiento' 
                    ? 'ring-2 ring-orange-500 shadow-lg scale-105' 
                    : 'hover:shadow-md hover:scale-102'
                }`}
                onClick={() => handleCardFilterClick('Actas de Oralidad de Seguimiento')}
              >
                <CardContent className="pt-6">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <FileText className="w-4 h-4 text-green-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-muted-foreground">Actas</p>
                      <p className="text-2xl font-bold text-green-600">{estadisticas.actasOralidad}</p>
                    </div>
                  </div>
                  {tipoFilter === 'Actas de Oralidad de Seguimiento' && (
                    <p className="text-xs text-muted-foreground mt-2">Filtro activo</p>
                  )}
                </CardContent>
              </Card>

              <Card 
                className={`transition-all cursor-pointer ${
                  tipoFilter === 'Resoluciones' 
                    ? 'ring-2 ring-orange-500 shadow-lg scale-105' 
                    : 'hover:shadow-md hover:scale-102'
                }`}
                onClick={() => handleCardFilterClick('Resoluciones')}
              >
                <CardContent className="pt-6">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                      <FileText className="w-4 h-4 text-purple-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-muted-foreground">Resoluciones</p>
                      <p className="text-2xl font-bold text-purple-600">{estadisticas.resoluciones}</p>
                    </div>
                  </div>
                  {tipoFilter === 'Resoluciones' && (
                    <p className="text-xs text-muted-foreground mt-2">Filtro activo</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Search Bar */}
            <div className="flex items-center space-x-2 mb-6">
              <Search className="w-4 h-4 text-gray-500" />
              <Input
                placeholder="Buscar documentos por nombre, tipo o archivo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1"
              />
            </div>

            {/* Documents List */}
            {filteredDocumentos.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-16">
                  <div className="text-center space-y-4">
                    <div className="w-16 h-16 bg-gray-100 rounded-full mx-auto flex items-center justify-center">
                      <FileText className="w-8 h-8 text-gray-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">
                      {searchTerm ? 'No se encontraron documentos' : 'No hay documentos aún'}
                    </h3>
                    <p className="text-gray-500 max-w-md">
                      {searchTerm 
                        ? 'Intenta con otros términos de búsqueda' 
                        : 'Comienza subiendo tu primer documento legal'
                      }
                    </p>
                    {!searchTerm && (
                      <Button onClick={() => setIsUploadModalOpen(true)}>
                        <Plus className="w-4 h-4 mr-2" />
                        Subir Primer Documento
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {filteredDocumentos.map((documento) => (
                  <Card key={documento.id} className="hover:shadow-md transition-shadow">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <CardTitle className="text-base font-medium truncate" title={documento.nombre_documento}>
                            {documento.nombre_documento}
                          </CardTitle>
                          <p className="text-xs text-muted-foreground mt-1 truncate" title={documento.nombre_archivo}>
                            {documento.nombre_archivo}
                          </p>
                        </div>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50">
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>¿Eliminar documento?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Esta acción no se puede deshacer. El documento "{documento.nombre_documento}" será eliminado permanentemente.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteDocument(documento)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Eliminar
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </CardHeader>
                    
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          {getTipoBadge(documento.tipo_documento)}
                        </div>
                        
                        <div className="space-y-2 text-xs text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            <span>{formatDate(documento.created_at)}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <FileText className="w-3 h-3" />
                            <span>{formatFileSize(documento.tamano_archivo)}</span>
                          </div>
                        </div>
                        
                         <Button
                           onClick={() => handleViewDocument(documento)}
                           className="w-full"
                           size="sm"
                         >
                           <Eye className="w-3 h-3 mr-1" />
                           Ver Documento
                         </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
        <Footer />
      </main>

      {/* Upload Document Modal */}
      <SubirDocumentoModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
      />

      {/* Document Viewer Modal */}
      <DocumentViewer
        documento={selectedDocument}
        isOpen={isViewerOpen}
        onClose={() => {
          setIsViewerOpen(false);
          setSelectedDocument(null);
        }}
      />
    </div>
  );
};

export default Legal;